/**
 * Location-access-denied response interceptor (JQ4-12 WU-24).
 *
 * When the backend returns 403 with code LOCATION_ACCESS_DENIED or
 * LOCATION_INACTIVE, the client-side state must be reset so the PWA
 * can re-prompt the user to pick a valid location.
 *
 * Responsibilities:
 *   1. Clear `jq4race:active_location_id` from localStorage.
 *   2. Dispatch a `jq4race:location-access-denied` CustomEvent on `window`
 *      so the app shell / boot gate can react (e.g., re-show LocationPicker).
 *
 * The caller (client.ts) is responsible for still throwing the ApiError after
 * calling this — we only handle the side-effects here.
 */

import { ACTIVE_LOCATION_STORAGE_KEY } from '@jq4race/shared';

export const LOCATION_DENIED_EVENT = 'jq4race:location-access-denied' as const;
export type LocationDeniedEvent = typeof LOCATION_DENIED_EVENT;

const LOCATION_DENIED_CODES = new Set(['LOCATION_ACCESS_DENIED', 'LOCATION_INACTIVE']);

/**
 * Handle a 403 response code from the backend.
 * No-op for codes that are not location-access errors.
 */
export function handleLocationAccessDenied(code: string): void {
  if (!LOCATION_DENIED_CODES.has(code)) return;

  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(ACTIVE_LOCATION_STORAGE_KEY);
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(LOCATION_DENIED_EVENT));
  }
}
